import { Component, Element, h, Host, Listen, Prop, State, Watch } from "@stencil/core";

import { debounce } from "../../utils/debounce";
import { getViewportWidth } from "../../utils/getViewportWidth";

/**
 * @slot (default) - The content of the accordion
 * @slot title - the element for the accordion title
 */
@Component({
    tag: "ux-accordion",
    styleUrl: "accordion.scss",
    shadow: false
})
export class Accordion {
    private baseClass = "ux-accordion";
    private contentContainer: HTMLDivElement;
    private titleContainer: HTMLDivElement;
    private title: Element;

    @Element() element: HTMLUxAccordionElement;

    /** The amount in pixels from wich the accordion functionality is deactivated */
    @Prop() readonly noAccordionFrom: number | null = null;
    /** The class to be passes to the title of the accordion when it becomes active */
    @Prop() readonly activeTitleClass: string = "ux-text-link--green";
    /** If a click event is detected on this selector, the accordion will not trigger */
    @Prop() readonly noClickSelector: string = "";
    /** Determines if accordion is opened on initial view */
    @Prop({
        mutable: true,
        reflect: true
    })
    isExpanded = false;

    @State() noAccordion = this.noAccordionFrom !== null ? window.innerWidth >= this.noAccordionFrom : false;
    @State() windowWidth: number = getViewportWidth();

    connectedCallback() {
        window.addEventListener("resize", this.onResize);
    }

    componentDidLoad() {
        this.title = this.titleContainer.firstElementChild;
        this.setIsExpanded();
    }

    private onResize = debounce(() => {
        this.windowWidth = getViewportWidth();
        this.setIsExpanded();
        if (!this.noAccordionFrom) {
            return;
        }
        this.noAccordion = this.windowWidth >= this.noAccordionFrom;
        if (this.noAccordion) {
            this.title.classList.remove(this.activeTitleClass);
        }
    }, 200);

    /**
     * Checks if the clicked element or its parentElement has any match with the given selector(tag, #id, .class)
     *
     * @param {string} selector - selector we are looking for
     * @param {object} clickedElement - the clicked event target
     * @return {boolean}
     */

    private matchingElement = (selector, clickedElement) => {
        return clickedElement.closest(selector);
    };

    @Watch("isExpanded")
    setIsExpanded() {
        this.setOrRemoveTitleClass();

        if (this.isExpanded) {
            this.contentContainer.style.maxHeight = this.contentContainer.scrollHeight + (this.windowWidth > 767 ? 65 : 50) + "px";
        } else {
            this.contentContainer.style.maxHeight = null;
        }
    }

    @Listen("uxCollapsibleExpanded", { target: "window" })
    handleCollapsibleExpansion() {
        this.onResize();
    }

    private setOrRemoveTitleClass() {
        if (this.activeTitleClass !== "") {
            if (this.isExpanded) {
                this.title.classList.add(this.activeTitleClass);
            } else {
                this.title.classList.remove(this.activeTitleClass);
            }
        }
    }

    private handleAccordionClick = (e: Event) => {
        const clickTarget = e.target as HTMLElement;

        if (this.noClickSelector !== "") {
            if (this.matchingElement(this.noClickSelector, clickTarget)) {
                return;
            }
        }

        if (this.noAccordion) {
            return;
        }

        this.isExpanded = !this.isExpanded;
    };

    private handleKeyDown(e: KeyboardEvent) {
        e.preventDefault();
        if (e.key === "Enter" || e.key === " ") {
            this.handleAccordionClick(e);
        }
    }

    disconnectedCallback() {
        window.removeEventListener("resize", this.onResize);
    }

    render() {
        return (
            <Host
                class={{
                    [this.baseClass]: true,
                    [`${this.baseClass}--active`]: this.isExpanded && !this.noAccordion
                }}
            >
                <div
                    class={{
                        [`${this.baseClass}__title-container`]: !this.noAccordion
                    }}
                    onClick={(e) => this.handleAccordionClick(e)}
                    onKeyDown={(e) => this.handleKeyDown(e)}
                    ref={(el) => (this.titleContainer = el)}
                    tabIndex={0}
                    aria-expanded={this.isExpanded}
                    role='button'
                >
                    <slot name='title' />
                    {!this.noAccordion && <ux-icon name='chevron-down' weight='bold'></ux-icon>}
                </div>
                <div
                    ref={(el) => (this.contentContainer = el)}
                    class={{
                        [`${this.baseClass}__content-container`]: !this.noAccordion
                    }}
                    aria-hidden={!this.isExpanded}
                >
                    <slot />
                </div>
            </Host>
        );
    }
}
